import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosResponse } from "axios";
import { 
  voteContestantRequest,
  createContestantRequest,
  deleteContestantRequest,
  evictContestantRequest,
  updateContestantRequest,
  fetchContestantsRequest
} from '../utils/apiRequests'
import { status, CONTESTANT_AVATAR_S3_FOLDER, seasonTypes } from "../utils/constants";
import { processError, retrieveErrorMessage, uploadImage } from '../utils/helpers'
import { Status, ContestantWithBio, ContestantResponse, ICategory } from '../utils/interfaces'
import creditStore from './creditStore'
import uiStore from './uiStore'
import competitionStore from './competitionStore';

type TVoteContestantPayload = {
  contestantUsername: string
  voteCount: number
  categoryId?: number
  seasonId: string
}

type TSaveContestantPayload = {
  payload: ContestantWithBio,
  creatingCompetition: boolean,
  currentSeasonId: string,
  categoryId?: number
  subscriptionPlan: number
}

class ContestantStore {
  contestants: ContestantWithBio[] = []
  contestantsStatus: Status = null

  addContestantFields = []
  voteStatus: Status = null

  saveContestantStatus: Status = null
  updateContestantStatus: Status = null
  deleteContestantStatus: Status = null

  constructor() {
    makeAutoObservable(this)
  }

  canAddContestant(plan?: number) {
    const subscriptionPlan = plan || seasonTypes.FREE
    if (!subscriptionPlan && subscriptionPlan !== 0) return false
    if (subscriptionPlan === seasonTypes.PREMIER || subscriptionPlan === seasonTypes.PRO) return true
    const options = competitionStore.getPlanByCode(subscriptionPlan)
    return this.contestants.length < (options?.max_contestants || 0)
  }

  async voteContestant({
    contestantUsername,
    voteCount,
    categoryId,
    seasonId
  }: TVoteContestantPayload) {
    if (this.votingContestant) return

    uiStore.setLoadingScreenMessage('Registering your vote(s)')
    runInAction(() => {
      this.voteStatus = status.LOADING
    })

    try {
      const response = await voteContestantRequest(contestantUsername, {
        season_id: seasonId,
        vote_count: voteCount,
        category_id: categoryId
      })
      creditStore.getUserCredit()
      uiStore.showToastMessage({message: response.data.message, type: 'success'})

      runInAction(() => {
        this.voteStatus = status.COMPLETE
      })
    } catch (e) {
      runInAction(() => {
        this.voteStatus = status.FAILED
      })

      processError(e)
      uiStore.errorModal(retrieveErrorMessage(e))
    }

    uiStore.setLoadingScreenMessage('')
  }

  async fetchContestants(competitionUsername:string, seasonId: string) {
    if (this.fetchingContestants) return
    runInAction(() => this.contestantsStatus = status.LOADING)

    try {
      const contestantsResponse = await fetchContestantsRequest(competitionUsername, seasonId)
      runInAction(() => {
        this.contestants = contestantsResponse.data
        this.contestantsStatus = status.COMPLETE
      })
    } catch (e) {
      processError(e)
      runInAction(() => this.contestantsStatus = status.FAILED)
    }
  }

  async saveContestant({ 
    payload, 
    creatingCompetition, 
    currentSeasonId, 
    subscriptionPlan, 
    categoryId 
  }: TSaveContestantPayload) {
    if (this.savingContestant) return
    const editMode = !!payload.id

    if (!this.canAddContestant(subscriptionPlan) && !editMode) {
      uiStore.errorModal('You have exceeded the maximum number of contestants, please upgrade your account plan.')
      return
    }

    let previousAvatar = null
    let response: AxiosResponse<ContestantResponse>

    if (editMode) previousAvatar = this.getContestant(payload.username, creatingCompetition)?.avatar
    
    runInAction(() => {
      this.saveContestantStatus = status.LOADING
    })

    try {
      if (!editMode || (editMode && previousAvatar !== payload.avatar)) {
        const s3Url = await uploadImage({
          key: payload.username,
          imageUrl: payload.avatar,
          folderPath: CONTESTANT_AVATAR_S3_FOLDER,
          async: false,
        })

        payload.avatar = s3Url
      }

      payload.season_id = currentSeasonId
      payload.category_id = categoryId

      response = await (editMode ? updateContestantRequest(payload) : createContestantRequest(payload))
      const contestant = {...response.data, ...response.data.bio, bio: undefined}

      if (editMode) {
        this.updateContestant(contestant, creatingCompetition)
      } else {
        this.addContestant(contestant, creatingCompetition)
      }
      
      runInAction(() => {
        this.saveContestantStatus = status.COMPLETE
      })
    } catch (e) {
      uiStore.errorModal(retrieveErrorMessage(e))
      runInAction(() => {
        this.saveContestantStatus = status.FAILED
      })
    }
  }

  async deleteContestant(season_id: string, contestantUsername: string, creatingCompetition: boolean=true) {
    uiStore.setLoadingScreenMessage('deleting contestant')

    try {
      await deleteContestantRequest({
        season_id,
        contestant_username: contestantUsername
      })

      runInAction(() => {
        uiStore.successModal('Contestant Deleted')
      })

      this.removeContestant(contestantUsername, creatingCompetition)
      // if (creatingCompetition) this.persistState()
    } catch (e) {
      runInAction(() => {
        uiStore.errorModal(retrieveErrorMessage(e))
      })
    }

    uiStore.setLoadingScreenMessage('')
  }

  async evictContestant(season_id: string, username: string) {
    uiStore.setLoadingScreenMessage('Evicting Contestant...')

    try {
      const response = await evictContestantRequest({ season_id, username })
      this.updateContestant(response.data, false)
    } catch (e) {
      uiStore.errorModal(retrieveErrorMessage(e))
    }

    uiStore.setLoadingScreenMessage('')
  }

  addContestant(contestant: ContestantWithBio, creatingCompetition: boolean = true) {
    this.contestants = this.contestants.concat(contestant)
  }

  removeContestant(contestantUsername: string, creatingCompetition: boolean) {
    this.contestants = this.contestants.filter(c => c.username !== contestantUsername)
  }

  updateContestant(updatedContestant: ContestantWithBio, creatingCompetition: boolean = true) {
    const updatedList: ContestantWithBio[] = []

    this.contestants.forEach(contestant => {
      if (contestant.id === updatedContestant.id) {
        updatedList.push(updatedContestant)
      } else {
        updatedList.push(contestant)
      }
    })

    this.contestants = updatedList
  }

  getContestant(contestantUsername: string, creatingCompetition?: boolean) {
    return this.contestants.find(contestant => contestant.username === contestantUsername)
  }

  clearContestants() {
    this.contestants = []
  }

  getContestantFilteredByCategory(category?: ICategory) {
    if (!category) return this.contestants
    return this.contestants.filter(contestant => {
      return category.id === contestant.category_id
    })
  }

  get votingContestant() {
    return this.voteStatus === status.LOADING
  }

  get savingContestant() {
    return this.saveContestantStatus === status.LOADING
  }

  get fetchingContestants() {
    return this.contestantsStatus === status.LOADING
  }
}

const contestantStore = new ContestantStore()
export default contestantStore
